import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Datos del invitado que persistimos tras una compra exitosa, para
 * autofillear el checkout en futuras compras del mismo navegador.
 *
 * NO incluye `identification` (no la pedimos en checkout) ni `address`
 * legacy (no la usamos). Los 5 campos de dirección son los nuevos.
 */
export interface GuestProfile {
  name?: string;
  surnames?: string;
  email?: string;
  phone?: string;
  province?: string;
  canton?: string;
  district?: string;
  hood?: string;
  directions?: string;
}

const STORAGE_KEY = 'perron_guest_profile';

/**
 * Persistencia del perfil del cliente invitado en localStorage.
 *
 * Política:
 *  - Solo se invoca SET tras una compra exitosa (no en cada keystroke).
 *  - GET solo se consulta cuando NO hay sesión activa (los logueados
 *    autocompletan desde su Client del backend).
 *  - SSR-safe: en server, getter devuelve null y setter es no-op.
 *  - Si el JSON guardado está corrupto, devolvemos null (graceful).
 */
@Injectable({ providedIn: 'root' })
export class GuestProfileService {
  private readonly platformId = inject(PLATFORM_ID);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  getProfile(): GuestProfile | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }
      return parsed as GuestProfile;
    } catch {
      return null;
    }
  }

  setProfile(profile: GuestProfile): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // Quota exceeded o modo privado: silencioso para no bloquear
      // el flujo de compra exitoso.
    }
  }

  clearProfile(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silencioso.
    }
  }
}
